import {
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

import { useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'

import { useToggyModerateBannerEnabled } from '../../../../featureFlags/hooks/useToggyModerateBannerEnabled.js'

import {
  dismissToggyBanner,
  dismissToggyPageBanner,
  isToggyBannerDismissed,
  isToggyPageBannerDismissed,
  TOGGY_BANNER_HREF,
  toggyBannerHeightRootCss,
} from './session.js'

const LogoMotion = lazy(() => import('./LogoMotion.js'))

const LOGO_MIN_WIDTH_QUERY = '(min-width: 640px)'

const ArrowRightIcon = () => (
  <svg
    className="tmb__cta-arrow"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const LivingGradient = () => (
  <div className="tmb__bg" aria-hidden>
    <div className="tmb__aurora" />
    <div className="tmb__orb tmb__orb--1" />
    <div className="tmb__orb tmb__orb--2" />
    <div className="tmb__orb tmb__orb--3" />
    <div className="tmb__orb tmb__orb--4" />
    <div className="tmb__orb tmb__orb--5" />
    <div className="tmb__sweep" />
    <div className="tmb__grain" />
    <div className="tmb__veil" />
  </div>
)

const ToggyMarkStatic = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <circle cx="9" cy="9" r="9" fill="#000" />
    <rect x="3.4" y="5.6" width="11.2" height="6.8" rx="3.4" fill="#fff" />
    <circle cx="6.6" cy="9" r="1.55" fill="#000" />
    <circle cx="11.4" cy="9" r="1.55" fill="#000" />
  </svg>
)

/** "Toggy" as a wordmark: the eye set right before the word, inside the sentence. */
const ToggyWordmark = ({
  animate,
  children,
}: {
  animate: boolean
  children: ReactNode
}) => (
  <span className="tmb__brand">
    <span className="tmb__mark" aria-hidden>
      {animate ? (
        <Suspense fallback={<ToggyMarkStatic />}>
          <LogoMotion state="idle" size={18} />
        </Suspense>
      ) : (
        <ToggyMarkStatic />
      )}
    </span>
    {children}
  </span>
)

const useMatchMedia = (query: string) => {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    setMatches(media.matches)
    media.addEventListener('change', onChange)

    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [query])

  return matches
}

type ToggyBannerVariant = 'strip' | 'page'

const ToggyBanner = ({ variant }: { variant: ToggyBannerVariant }) => {
  const enabled = useToggyModerateBannerEnabled()
  const { isAdmin } = useAuthMember()
  const { $t } = useI18n()
  const isPage = variant === 'page'
  // Reserve the strip on SSR and first paint so the common (non-dismissed)
  // case never inserts 48px after hydration. sessionStorage is client-only,
  // so dismissed viewers collapse after layout — the rare case.
  // Page: no reserved height, so a dismissed viewer who hard-reloads the
  // queue gets a one-time collapse as the card leaves the server HTML.
  // Client-side navigation collapses before paint.
  const [visible, setVisible] = useState(true)
  const reducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)')
  const showLogo = useMatchMedia(LOGO_MIN_WIDTH_QUERY)
  const hasLogo = showLogo && !reducedMotion

  useLayoutEffect(() => {
    const dismissed = isPage
      ? isToggyPageBannerDismissed()
      : isToggyBannerDismissed()
    if (dismissed) {
      setVisible(false)
    }
  }, [isPage])

  const handleDismiss = useCallback(() => {
    if (isPage) {
      dismissToggyPageBanner()
    } else {
      dismissToggyBanner()
    }
    setVisible(false)
  }, [isPage])

  if (!enabled || !isAdmin || !visible) {
    return null
  }

  const productName = isPage
    ? $t({
        defaultMessage: 'Toggy Moderate',
        id: 'Admin.Moderation.ToggyBanner.Name',
      })
    : $t({
        defaultMessage: 'Toggy',
        id: 'Admin.TopNavBar.ToggyBanner.Growth.Name',
      })

  const banner = (
    <div
      className={isPage ? 'tmb tmb--page' : 'tmb'}
      role="region"
      aria-label={productName}
    >
      <LivingGradient />

      {/* Strip: the mark leads the row. Page: it sits inline, opening the question. */}
      {hasLogo && !isPage && (
        <div className="tmb__logo">
          <div className="tmb__logo-glow" aria-hidden />
          <Suspense fallback={null}>
            <LogoMotion state="idle" size={28} />
          </Suspense>
        </div>
      )}

      {/* The strip already announces "New"; the in-page card stays quiet. */}
      {!isPage && (
        <span className="tmb__badge">
          {$t({
            defaultMessage: 'New',
            id: 'Generics.New',
          })}
        </span>
      )}

      <div className="tmb__text">
        {!isPage && <span className="tmb__name">{productName}</span>}
        <span className="tmb__message">
          <span className="tmb__headline">
            {isPage
              ? $t({
                  defaultMessage: 'Still moderating every post manually?',
                  id: 'Admin.Moderation.ToggyBanner.Headline',
                })
              : $t({
                  defaultMessage: 'Grow your community. Keep it healthy.',
                  id: 'Admin.TopNavBar.ToggyBanner.Growth.Headline',
                })}
          </span>
          <span className="tmb__supporting">
            {isPage
              ? $t({
                  defaultMessage:
                    'Toggy reviews every post, reply, and member profile automatically, with every decision synced to your moderation queue.',
                  id: 'Admin.Moderation.ToggyBanner.Supporting',
                })
              : $t({
                  defaultMessage:
                    'Engage members, spark conversations, and moderate every post with AI.',
                  id: 'Admin.TopNavBar.ToggyBanner.Growth.Supporting',
                })}
          </span>
        </span>
      </div>

      <a
        className="tmb__cta"
        href={TOGGY_BANNER_HREF}
        target="_blank"
        rel="noopener noreferrer"
      >
        {isPage ? (
          // The mark lives in the action: "Try ◉ Toggy →", wherever a locale puts the word.
          <>
            {$t(
              {
                defaultMessage: 'Try <toggy>Toggy</toggy>',
                id: 'Admin.Moderation.ToggyBanner.Cta',
              },
              {
                toggy: chunks => (
                  <ToggyWordmark animate={hasLogo}>{chunks}</ToggyWordmark>
                ),
              },
            )}
            <ArrowRightIcon />
          </>
        ) : (
          <>
            {$t({
              defaultMessage: 'Learn more',
              id: 'Generics.LearnMore',
            })}
            <ArrowRightIcon />
          </>
        )}
      </a>

      <button
        type="button"
        className="tmb__dismiss"
        onClick={handleDismiss}
        aria-label={
          isPage
            ? $t({
                defaultMessage: 'Dismiss Toggy Moderate banner',
                id: 'Admin.Moderation.ToggyBanner.Dismiss',
              })
            : $t({
                defaultMessage: 'Dismiss Toggy banner',
                id: 'Admin.TopNavBar.ToggyBanner.Growth.Dismiss',
              })
        }
      >
        <CloseIcon />
      </button>
    </div>
  )

  if (isPage) {
    return banner
  }

  return (
    <>
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: toggyBannerHeightRootCss() }} />
      {banner}
    </>
  )
}

export const ToggyModerateStripBanner = () => <ToggyBanner variant="strip" />

export const ToggyModerationPageBanner = () => <ToggyBanner variant="page" />
